import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSectionNavigation } from '../../hooks/useSectionNavigation';
import { trackEvent } from '../../analytics/tracker';
import { GlobalSettings } from '../../types';
import { motion } from 'framer-motion';
import { ArrowRight, Globe, Smartphone, Monitor } from 'lucide-react';

interface HeroProps {
  settings: GlobalSettings | null;
}

const Hero: React.FC<HeroProps> = ({ settings }) => {
  const { t, language } = useLanguage();
  const { scrollToSection } = useSectionNavigation();

  const getS = (key: keyof GlobalSettings, fallbackKey: string) => {
    if (!settings) return t(fallbackKey);
    const val = language === 'ar' ? settings[`${key}_ar` as keyof GlobalSettings] : settings[key];
    return (val as string) || t(fallbackKey);
  };

  return (
    <section
      id="home"
      className="sw-hero"
      role="region"
      aria-labelledby="hero-title"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-[120px]"
          style={{ backgroundColor: 'rgba(var(--primary-rgb), 0.12)' }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full blur-[120px]"
          style={{ backgroundColor: 'rgba(var(--accent-rgb), 0.08)' }}
        />
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: 'radial-gradient(var(--text) 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      <div className="sw-container relative z-10">
        <div className="hero-grid">
          <div className="hero-text" data-sw-reveal="true">
            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
              style={{
                backgroundColor: 'rgba(var(--success-rgb), 0.1)',
                border: '1px solid rgba(var(--success-rgb), 0.2)'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: 'var(--success)' }}></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ backgroundColor: 'var(--success)' }}></span>
              </span>
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--success)' }}>{t('hero_eyebrow')}</span>
            </motion.div>

            {/* Title */}
            <h1 className="hero-title" id="hero-title">
              <motion.span
                className="block"
                style={{ color: 'var(--text)' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {getS('hero_title_1', 'hero_title_1')}
              </motion.span>
              <motion.span
                className="block gradient-text py-1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                {getS('hero_title_2', 'hero_title_2')}
              </motion.span>
              <motion.span
                className="block"
                style={{ color: 'var(--text)' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                {getS('hero_title_3', 'hero_title_3')}
              </motion.span>
            </h1>

            {/* Description */}
            <motion.p
              className="hero-lead"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              {getS('hero_lead', 'hero_lead')}
            </motion.p>

            {/* CTAs */}
            <motion.div
              className="hero-actions"
              role="group"
              aria-label="Hero actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <a
                href="#contact"
                className="sw-btn sw-btn-primary btn-lg"
                onClick={(e) => {
                  trackEvent('cta_click', undefined, { label: 'get_started', location: 'hero' });
                  scrollToSection(e, 'contact');
                }}
                aria-describedby="hero-cta-desc"
              >
                {t('btn_launch_project')}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
              <a
                href="#websites"
                className="sw-btn sw-btn-secondary btn-lg"
                onClick={(e) => {
                  trackEvent('cta_click', undefined, { label: 'explore_work', location: 'hero' });
                  scrollToSection(e, 'websites');
                }}
              >
                {t('btn_explore_work')}
              </a>
            </motion.div>
            <span id="hero-cta-desc" className="sr-only">
              {t('hero_cta_desc') || 'Click to start your project with us'}
            </span>

            {/* Trust Indicators */}
            <motion.div
              className="flex flex-wrap gap-6 mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              role="group"
              aria-label="Trust indicators"
            >
              {[
                { text: t('services_eyebrow') || 'Web Development', icon: <Globe /> },
                { text: t('nav_pos') || 'SwiftPOS', icon: <Monitor /> },
                { text: 'Mobile Apps', icon: <Smartphone /> },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span style={{ color: 'var(--primary)' }}>{React.cloneElement(item.icon, { size: 16 })}</span>
                  <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>{item.text}</span>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="hero-visual" aria-hidden="true">
            <div className="visual-container sw-glass">
              <div className="floating-orb orb-1"></div>
              <div className="floating-orb orb-2"></div>
              <div className="hero-product-showcase">
                {/* Product UI Preview */}
                <div className="product-card">
                  <div className="product-card-header">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'rgba(var(--error-rgb), 0.4)' }} />
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'rgba(var(--warning-rgb), 0.4)' }} />
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'rgba(var(--success-rgb), 0.4)' }} />
                    </div>
                    <span className="text-[8px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>SwiftTech</span>
                  </div>
                  <div className="product-card-body">
                    <div className="product-stat">
                      <span className="text-[9px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>Projects</span>
                      <span className="text-lg font-black" style={{ color: 'var(--primary)' }}>47+</span>
                    </div>
                    <div className="product-stat">
                      <span className="text-[9px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>Clients</span>
                      <span className="text-lg font-black" style={{ color: 'var(--accent)' }}>120+</span>
                    </div>
                    <div className="product-stat">
                      <span className="text-[9px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>Rating</span>
                      <span className="text-lg font-black" style={{ color: 'var(--secondary)' }}>4.9★</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
